import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ASSET } from "../lib/api";
import { useLang } from "../contexts/LangContext";
import { useCart } from "../contexts/CartContext";
import { Package, Boxes, ShoppingBag, Store } from "lucide-react";

/**
 * Card for a vendor product. Shows image, name, category, channel badge
 * (B2C "a pezzi" / B2B "a blocchi"), price and availability.
 * B2C ("a pezzi") products can be added to the cart directly.
 */
export default function VendorProductCard({ product, showStore = false }) {
  const { formatPrice } = useLang();
  const { add } = useCart();
  const isBlocks = product.sale_mode === "blocchi";
  const hasVariants = Array.isArray(product.variants) && product.variants.length > 0;
  const isCaviar = product.category === "Caviale" || product.store_slug === "cutcaviar";
  const [variant, setVariant] = useState(hasVariants ? product.variants[0] : null);
  const img = product.image_url
    ? ASSET(product.image_url)
    : "https://images.unsplash.com/photo-1553546895-531931aa1aa8?w=600&q=60";

  const currentPrice = variant ? variant.price : product.price;
  const price = formatPrice ? formatPrice(currentPrice) : `€ ${currentPrice}`;
  const inStock = product.quantity > 0;

  const addToCart = () => {
    add({
      product_id: product.id,
      product_type: "vendor",
      name: variant ? `${product.name} · ${variant.label}` : product.name,
      image: img,
      unit_price: currentPrice,
      quantity: 1,
      unit: variant ? variant.label : "pz",
    });
  };

  return (
    <div
      data-testid="vendor-product-card"
      className="group border border-border bg-card rounded-sm overflow-hidden hover:shadow-lg transition-all duration-300"
    >
      <Link to={`/prodotto/${product.id}`} className="block relative aspect-[4/3] overflow-hidden bg-[hsl(var(--muted))]" data-testid="vendor-product-link">
        <img
          src={img}
          alt={product.name}
          onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = "https://images.unsplash.com/photo-1553546895-531931aa1aa8?w=600&q=60"; }}
          className={`w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 ${isCaviar ? "scale-[1.45] object-[center_70%] group-hover:scale-[1.52]" : ""}`}
        />
        <span
          className={`absolute top-3 left-3 inline-flex items-center gap-1.5 px-2.5 py-1 text-[10px] uppercase tracking-[0.15em] font-semibold rounded-sm ${
            isBlocks
              ? "bg-[#0a0a0a] text-[#C9A961] border border-[#C9A961]/40"
              : "bg-[hsl(var(--primary))] text-white"
          }`}
        >
          {isBlocks ? <Boxes className="h-3 w-3" /> : <Package className="h-3 w-3" />}
          {isBlocks ? "B2B · a blocchi" : "B2C · a pezzi"}
        </span>
      </Link>
      <div className="p-4">
        {product.category && (
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-1">
            {product.category}
          </div>
        )}
        <Link to={`/prodotto/${product.id}`} className="block">
          <h3 className="font-serif text-lg leading-tight text-foreground line-clamp-2 hover:text-[hsl(var(--primary))] transition-colors">
            {product.name}
          </h3>
        </Link>
        {product.description && (
          <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{product.description}</p>
        )}
        {showStore && product.store_slug && (
          <Link
            to={`/negozio/${product.store_slug}`}
            className="mt-2 inline-block text-xs text-[hsl(var(--primary))] hover:underline"
          >
            {product.store_name || product.seller_company}
          </Link>
        )}
        <div className="mt-3 flex items-end justify-between">
          <div>
            <div className="text-xl font-semibold text-[hsl(var(--primary))]">{price}</div>
            {isBlocks && (
              <div className="text-[11px] text-muted-foreground">
                {product.block_size} pz/blocco · min {product.min_order_blocks || 1} blocchi
              </div>
            )}
          </div>
          <div className="text-right">
            <div
              className={`text-xs font-semibold ${
                product.quantity > 0 ? "text-emerald-600" : "text-destructive"
              }`}
            >
              {product.quantity > 0 ? `${product.quantity} disp.` : "Esaurito"}
            </div>
          </div>
        </div>

        {/* Menu a tendina grammatura (varianti) */}
        {hasVariants && !isBlocks && (
          <div className="mt-3">
            <label className="sr-only" htmlFor={`variant-${product.id}`}>Grammatura</label>
            <select
              id={`variant-${product.id}`}
              data-testid="vendor-variant-select"
              value={variant ? variant.label : ""}
              onChange={(e) => setVariant(product.variants.find((v) => v.label === e.target.value) || product.variants[0])}
              className="w-full px-3 py-2 border border-border rounded-sm bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary))]/30"
            >
              {product.variants.map((v) => (
                <option key={v.label} value={v.label}>
                  {v.label} — {formatPrice ? formatPrice(v.price) : `€ ${v.price}`}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* CTA */}
        <div className="mt-4">
          {isBlocks ? (
            <Link
              to={product.store_slug ? `/negozio/${product.store_slug}` : "/negozi"}
              data-testid="vendor-b2b-cta"
              className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 border border-[#C9A961] text-[#8a6d1f] rounded-sm text-xs uppercase tracking-[0.15em] font-semibold hover:bg-[#C9A961]/10 transition-colors"
            >
              <Store className="h-4 w-4" /> Solo B2B · vedi in vetrina
            </Link>
          ) : (
            <button
              data-testid="vendor-add-to-cart"
              onClick={addToCart}
              disabled={!inStock}
              className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-[hsl(var(--primary))] text-white rounded-sm text-xs uppercase tracking-[0.15em] font-semibold hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity"
            >
              <ShoppingBag className="h-4 w-4" /> {inStock ? "Aggiungi al carrello" : "Esaurito"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
